import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Button, SegmentedControl } from '@liftango/ui-elements';
import { Icon } from '@liftango/ui-icons';
import { opsColors, opsFontFamily, opsFontSize, opsSpace } from '../../../../../theme/opsTokens';
import { requestBuckets, type RideTiming, type ServiceDay } from '../../../../../services/simulation/runReport';
import type { UnmatchedRide } from '../../../../../types/simulation';
import { formatClock } from './reportFormat';
import { hourTicks } from './chartData';
import { PLAY_SPEEDS, type Playback, type PlaySpeed } from './usePlayback';

const HISTOGRAM_HEIGHT = '40px';
const HISTOGRAM_BUCKET_MINUTES = 15;
const AXIS_STEP_HOURS = 2;
const NOW_LINE = '2px';
const ICON_SIZE = 16;
const MINUTE_MS = 60_000;
const HAIRLINE = '1px';
const FOCUS_RING = '2px solid';
const FOCUS_OFFSET = '2px';

/** Centres a label on its tick, but keeps the first and last labels inside the axis. */
const tickShift = (pct: number) => (pct <= 0 ? 'none' : pct >= 100 ? 'translateX(-100%)' : 'translateX(-50%)');
const isPlaySpeed = (value: number): value is PlaySpeed => (PLAY_SPEEDS as readonly number[]).includes(value);

export interface PlaybackBarProps {
  readonly day: ServiceDay;
  readonly rides: readonly RideTiming[];
  readonly unmatched: readonly UnmatchedRide[];
  readonly playback: Playback;
}

export function PlaybackBar({ day, rides, unmatched, playback }: PlaybackBarProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const buckets = useMemo(() => requestBuckets(rides, unmatched, day, HISTOGRAM_BUCKET_MINUTES), [rides, unmatched, day]);
  const ticks = useMemo(() => hourTicks(day, AXIS_STEP_HOURS), [day]);
  const peak = buckets.reduce((max, bucket) => Math.max(max, bucket.matched + bucket.unmatched), 0);
  const timeMs = playback.timeMs ?? day.startMs;
  const nowPct = ((timeMs - day.startMs) / (day.endMs - day.startMs)) * 100;

  return (
    <Box
      data-testid="sim-results.report.playback"
      sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', alignItems: 'center', gap: opsSpace[3], px: opsSpace[4], py: opsSpace[3], borderTop: `${HAIRLINE} solid ${opsColors.neutral.lineStrong}` }}
    >
      <Button
        kind="primary"
        aria-label={t(playback.playing ? 'results.report.playback.pause' : 'results.report.playback.play')}
        icon={<Icon name={playback.playing ? 'pause' : 'play'} size={ICON_SIZE} />}
        onClick={playback.togglePlay}
      />
      <SegmentedControl
        aria-label={t('results.report.playback.speed')}
        value={String(playback.speed)}
        onChange={(value) => {
          const speed = Number(value);
          if (isPlaySpeed(speed)) playback.setSpeed(speed);
        }}
        options={PLAY_SPEEDS.map((speed) => ({ value: String(speed), label: t('results.report.playback.speedOption', { count: speed }) }))}
      />
      <Box>
        <Box
          sx={{
            position: 'relative',
            height: HISTOGRAM_HEIGHT,
            display: 'flex',
            alignItems: 'flex-end',
            gap: HAIRLINE,
            // The range input is invisible, so its focus shows on the histogram it covers.
            '&:focus-within': { outline: `${FOCUS_RING} ${opsColors.brand.primary}`, outlineOffset: FOCUS_OFFSET },
          }}
        >
          {buckets.map((bucket) => {
            const total = bucket.matched + bucket.unmatched;
            return (
              <Box
                key={bucket.startMs}
                aria-hidden="true"
                sx={{ flex: 1, height: `${peak ? (total / peak) * 100 : 0}%`, backgroundColor: bucket.startMs <= timeMs ? opsColors.semantic.info.color : opsColors.neutral.lineStrong }}
              />
            );
          })}
          {/* Per-frame position goes in style: a changing sx value adds a CSS rule every frame. */}
          <Box aria-hidden="true" style={{ left: `${nowPct}%` }} sx={{ position: 'absolute', top: 0, bottom: 0, width: NOW_LINE, backgroundColor: opsColors.brand.primary }} />
          <Box
            component="input"
            type="range"
            data-testid="sim-results.report.playback.scrubber"
            aria-label={t('results.report.playback.scrubber')}
            aria-valuetext={formatClock(timeMs, locale)}
            min={Math.floor(day.startMs / MINUTE_MS) * MINUTE_MS}
            max={day.endMs}
            step={MINUTE_MS}
            value={timeMs}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => playback.seek(Number(event.target.value))}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', m: 0, opacity: 0, cursor: 'pointer' }}
          />
        </Box>
        <Box aria-hidden="true" sx={{ position: 'relative', height: opsSpace[4], mt: opsSpace[1] }}>
          {ticks.map((tick) => (
            <Box
              key={tick.ms}
              component="span"
              sx={{ position: 'absolute', left: `${tick.pct}%`, transform: tickShift(tick.pct), fontFamily: opsFontFamily.mono, fontSize: opsFontSize.eyebrow, color: opsColors.neutral.inkMute }}
            >
              {formatClock(tick.ms, locale)}
            </Box>
          ))}
        </Box>
        <Box sx={{ fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute }}>{t('results.report.playback.hint')}</Box>
      </Box>
    </Box>
  );
}
